import type { ZoneCode } from "@/domain/zones";

/**
 * Mouse map geometry, in key units (1u = one keycap). Hand-authored from design
 * handoff v2 section 5: the mouse map is geometry-as-design, one fixed
 * illustration of the zone namespace, so the importer-is-the-source rule
 * does not apply. geometry.test.ts pins these numbers to the design source.
 */
export const MOUSE_FRAME = {
  /** Full box width, including the left inset the thumb buttons protrude into. */
  width: 3.7,
  height: 6.2,
  /** The body starts this far in; the thumb buttons stick out to its left. */
  bodyInset: 0.34,
  channelWidth: 0.58,
  /** LMB/RMB shells and the wheel groove run from the top edge to here. */
  buttonsEnd: 2.46,
  /** The visual split line between the button shells and the palm. */
  splitLine: 2.56,
} as const;

/**
 * Width each annotation chip column reserves. The README's section 5 text names
 * the reservation rule; the widths come from the prototype component
 * (keylens-mouse.jsx reserveLeft 2.0u / reserveRight 2.2u), the construction's
 * source of truth for chip columns.
 */
export const CHIP_RESERVE = { left: 2.0, right: 2.2 } as const;

const { width: W, bodyInset: INSET, channelWidth: CH, buttonsEnd: BTN_END } = MOUSE_FRAME;
const SHELL_W = (W - INSET - 0.16 - CH) / 2;

/** Left edge of the wheel channel, absolute in the mouse box. */
export const CHANNEL_LEFT = INSET + 0.08 + SHELL_W;

/** One bindable zone's rectangle, absolute in the mouse box (chip columns excluded). */
export interface ZoneSpec {
  code: ZoneCode;
  /** On-graphic label; small zones show only this and rely on annotation chips. */
  label: string;
  small: boolean;
  /** Rendered inside the body-silhouette clip (shells and wheel), or over it (thumbs). */
  clipped: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}

export const MOUSE_ZONES: ZoneSpec[] = [
  {
    code: "MouseLeft",
    label: "LMB",
    small: false,
    clipped: true,
    x: INSET,
    y: 0,
    w: CHANNEL_LEFT - 0.04 - INSET,
    h: BTN_END,
  },
  {
    code: "MouseRight",
    label: "RMB",
    small: false,
    clipped: true,
    x: CHANNEL_LEFT + CH + 0.04,
    y: 0,
    w: W - (CHANNEL_LEFT + CH + 0.04),
    h: BTN_END,
  },
  {
    code: "MouseMiddle",
    label: "MMB",
    small: true,
    clipped: true,
    x: CHANNEL_LEFT + 0.06,
    y: 0.82,
    w: CH - 0.12,
    h: 1.0,
  },
  {
    code: "WheelUp",
    label: "↑",
    small: true,
    clipped: true,
    x: CHANNEL_LEFT + 0.06,
    y: 0.2,
    w: CH - 0.12,
    h: 0.5,
  },
  {
    code: "WheelDown",
    label: "↓",
    small: true,
    clipped: true,
    x: CHANNEL_LEFT + 0.06,
    y: 1.94,
    w: CH - 0.12,
    h: 0.5,
  },
  // Thumb side-buttons. The top of the illustration is the FRONT of the mouse (the wheel/LMB/RMB end),
  // so the lower thumb slot (larger y) is the BACK button and the upper slot is the FORWARD button.
  // Mouse4 is the BACK side button (XBUTTON1), Mouse5 the FORWARD (XBUTTON2), so M4 sits low and M5
  // high. Design source v2 section 5 drew them inverted (M4 at 2.74 above M5 at 3.82); corrected per
  // maintainer direction (2026-06-16), the same kind of section-5 deviation recorded for the
  // wheel chips. geometry.test.ts pins the corrected numbers.
  { code: "Mouse4", label: "M4", small: true, clipped: false, x: 0, y: 3.82, w: 0.52, h: 1.0 },
  { code: "Mouse5", label: "M5", small: true, clipped: false, x: 0, y: 2.74, w: 0.52, h: 1.0 },
];

/** An annotation chip's anchor: which zone it names, which column, hand-tuned center y. */
export interface ChipSpec {
  code: ZoneCode;
  /** Short mono prefix inside the chip pill. */
  tag: string;
  side: "left" | "right";
  cy: number;
}

// The wheel scroll chips (Scroll Up / Scroll Down) are NOT straight chips: their connectors wrap
// the RMB (see WHEEL_WRAP), so they live there. These are the straight-stub chips only.
export const MOUSE_CHIPS: ChipSpec[] = [
  // each chip tracks its zone: Mouse4 (BACK) is the lower slot, Mouse5 (FORWARD) the upper (see
  // MOUSE_ZONES; the design-source order was inverted, corrected 2026-06-16)
  { code: "Mouse4", tag: "M4", side: "left", cy: 4.32 },
  { code: "Mouse5", tag: "M5", side: "left", cy: 3.24 },
  { code: "MouseMiddle", tag: "●", side: "right", cy: 1.34 },
];

/** Connector stub width (units) from a straight annotation chip's pill to the mouse edge. */
export const CHIP_STUB = 0.36;

/** One wheel scroll chip's connector route around the RMB. */
export interface WheelWrapSpec {
  code: ZoneCode;
  /** The wheel zone's near edge the connector enters (units): top edge of ↑, bottom edge of ↓. */
  zoneY: number;
  /** The horizontal-run height (units); negative = above the mouse (Scroll Up wraps over the top). */
  runY: number;
}

/**
 * Scroll Up / Scroll Down sit in the central wheel groove, far from the right-hand annotation
 * column; a straight stub connector dies at the mouse edge and reads as pointing at the RMB. So
 * their connectors wrap the RMB instead - up and over the top for Scroll Up, down and under the
 * bottom for Scroll Down - arriving at the wheel zone so the pill clearly names the wheel.
 *
 * Maintainer direction (2026-06-16); a deliberate deviation from design source v2 section 5 (which
 * stacked all three wheel chips on the right with straight stubs). `zoneY` ties to the
 * zone edges in MOUSE_ZONES (geometry.test.ts pins the invariant).
 */
export const WHEEL_WRAP: WheelWrapSpec[] = [
  { code: "WheelUp", zoneY: 0.2, runY: -0.18 },
  { code: "WheelDown", zoneY: 2.44, runY: 2.72 },
];
